import { StrategyDailyMetric } from '../../../../types/Ad.api.type';
import { Card } from '../../../ui/card';

interface MetricsTableProps {
  data: StrategyDailyMetric[];
  onRowClick?: (metric: StrategyDailyMetric) => void;
}

export const MetricsTable = ({ data, onRowClick }: MetricsTableProps) => {
  // 합산 계산
  const sum = {
    rewardWatch: data.reduce((s, d) => s + d.rewardWatch, 0),
    activeUsers: data.reduce((s, d) => s + d.activeUsers, 0),
    totalVotes: data.reduce((s, d) => s + d.totalVotes, 0),
    totalAttendance: data.reduce((s, d) => s + d.totalAttendance, 0),
    tossRevenue: data.reduce((s, d) => s + (d.tossRevenue ?? 0), 0),
    tossImpressions: data.reduce((s, d) => s + (d.tossImpressions ?? 0), 0),
    newUsers: data.reduce((s, d) => s + (d.newUsers ?? 0), 0),
    actualSignups: data.reduce((s, d) => s + d.actualSignups, 0),
  };
  const avgAdPerUser = sum.activeUsers > 0 ? (sum.rewardWatch / sum.activeUsers).toFixed(2) : '0';
  const avgEcpm = (() => {
    const ecpmRows = data.filter(d => d.tossEcpm != null);
    if (ecpmRows.length === 0) return null;
    return Math.round(ecpmRows.reduce((s, d) => s + d.tossEcpm!, 0) / ecpmRows.length);
  })();
  const avgCtr = (() => {
    const ctrRows = data.filter(d => d.tossCtr != null);
    if (ctrRows.length === 0) return null;
    return (ctrRows.reduce((s, d) => s + d.tossCtr!, 0) / ctrRows.length).toFixed(2);
  })();
  const avgD1 = (() => {
    const d1Rows = data.filter(d => d.retentionD1New != null);
    if (d1Rows.length === 0) return null;
    return (d1Rows.reduce((s, d) => s + d.retentionD1New!, 0) / d1Rows.length).toFixed(0);
  })();
  const lastTotal = data.filter(d => d.totalUsers != null).at(-1)?.totalUsers ?? null;

  return (
  <Card>
    <div className="p-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">전체 데이터 테이블</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 pr-2 whitespace-nowrap">날짜</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">리워드</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">1인당</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">활성유저</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">투표</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">출석</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">eCPM</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">토스수익</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">CTR</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">노출</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">신규(토스)</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">실제가입</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">전체</th>
              <th className="pb-2 pr-2 text-right whitespace-nowrap">D1신규</th>
              <th className="pb-2 text-center whitespace-nowrap">배포</th>
            </tr>
          </thead>
          <tbody>
            {data.map(d => (
              <tr
                key={d.date}
                className={`border-b last:border-0 hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick?.(d)}
              >
                <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{d.date.slice(5)}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{d.rewardWatch}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{d.adWatchPerUser}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{d.activeUsers}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{d.totalVotes}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{d.totalAttendance}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{d.tossEcpm ?? '-'}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{d.tossRevenue ?? '-'}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{d.tossCtr != null ? `${d.tossCtr}%` : '-'}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{d.tossImpressions ?? '-'}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{d.newUsers ?? '-'}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{d.actualSignups}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{d.totalUsers ?? '-'}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{d.retentionD1New != null ? `${d.retentionD1New}%` : '-'}</td>
                <td className="py-1.5 text-center">
                  {d.deployVersion && (
                    <span className="inline-block bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded" title={d.deployNote ?? ''}>
                      {d.deployVersion}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {/* 합산 행 */}
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td className="py-2 pr-2 whitespace-nowrap">합계</td>
              <td className="py-2 pr-2 text-right font-mono">{sum.rewardWatch.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right font-mono">{avgAdPerUser}</td>
              <td className="py-2 pr-2 text-right font-mono">{sum.activeUsers.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right font-mono">{sum.totalVotes.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right font-mono">{sum.totalAttendance.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right font-mono text-gray-400">{avgEcpm != null ? avgEcpm.toLocaleString() : '-'}</td>
              <td className="py-2 pr-2 text-right font-mono text-gray-400">{sum.tossRevenue > 0 ? sum.tossRevenue.toLocaleString() : '-'}</td>
              <td className="py-2 pr-2 text-right font-mono text-gray-400">{avgCtr != null ? `${avgCtr}%` : '-'}</td>
              <td className="py-2 pr-2 text-right font-mono text-gray-400">{sum.tossImpressions > 0 ? sum.tossImpressions.toLocaleString() : '-'}</td>
              <td className="py-2 pr-2 text-right font-mono text-gray-400">{sum.newUsers > 0 ? sum.newUsers.toLocaleString() : '-'}</td>
              <td className="py-2 pr-2 text-right font-mono">{sum.actualSignups.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right font-mono text-gray-400">{lastTotal != null ? lastTotal.toLocaleString() : '-'}</td>
              <td className="py-2 pr-2 text-right font-mono text-gray-400">{avgD1 != null ? `${avgD1}%` : '-'}</td>
              <td className="py-2"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </Card>
  );
};
